// ============================================================================
// 🔄 SCRIPT: SINCRONIZZAZIONE RAPPORTINI (v2.4 FINAL - FIX TIMESTAMP)
// ============================================================================
//
// 🔧 FIX v2.4: Conversione corretta Timestamp Firebase → Date
// - BUG v2.3: new Date(rapportino.data) generava date sbagliate (1999-12-31)
// - FIX v2.4: Usa rapportino.data.toDate() per Timestamp Firebase
//
// 🎯 Questa è la versione DEFINITIVA E FUNZIONANTE
// ============================================================================

import { 
  collection, 
  getDocs,
  getDoc,
  updateDoc, 
  doc, 
  query,
  where,
  writeBatch 
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAllUsers } from '../firebase';

/**
 * 🆕 HELPER: Converti data Firebase in YYYY-MM-DD
 */
const formatDateToYYYYMMDD = (firebaseDate) => {
  let dateObj;
  
  // Gestisci Timestamp Firebase
  if (firebaseDate && firebaseDate.toDate && typeof firebaseDate.toDate === 'function') {
    dateObj = firebaseDate.toDate();
  } 
  // Gestisci Date object
  else if (firebaseDate instanceof Date) {
    dateObj = firebaseDate;
  } 
  // Gestisci stringa o altro
  else {
    dateObj = new Date(firebaseDate);
  }
  
  // Converti in YYYY-MM-DD
  return dateObj.toISOString().split('T')[0];
};

/**
 * 🔍 HELPER: Recupera ore da workHours
 */
const getWorkHoursForDate = async (userId, date) => {
  try {
    const dateObj = new Date(date);
    const month = (dateObj.getMonth() + 1).toString();
    const year = dateObj.getFullYear().toString();

    const docId = `${userId}_${month}_${year}`;
    const workHoursRef = doc(db, 'workHours', docId);
    const workHoursSnap = await getDoc(workHoursRef);

    if (!workHoursSnap.exists()) {
      return {
        found: false,
        reason: 'Documento workHours non trovato',
        isSpecialDay: false,
        oreStandard: 0,
        oreStraordinario: 0,
        oreTotali: 0
      };
    }

    const data = workHoursSnap.data();
    const entries = data.entries || [];
    
    // Cerca entry per date (YYYY-MM-DD)
    const dayEntry = entries.find(e => e.date === date);
    
    if (!dayEntry) {
      return {
        found: false,
        reason: 'Entry non trovata per questa data',
        isSpecialDay: false,
        oreStandard: 0,
        oreStraordinario: 0,
        oreTotali: 0
      };
    }

    // Giorni speciali (M, P, F, A, C)
    if (typeof dayEntry.total === 'string' && ['M', 'P', 'A', 'C', 'F'].includes(dayEntry.total)) {
      return {
        found: true,
        isSpecialDay: true,
        specialCode: dayEntry.total,
        oreStandard: 0,
        oreStraordinario: 0,
        oreTotali: 0
      };
    }

    // Ore normali: total + overtime
    const oreStandard = parseFloat(dayEntry.total) || 0;
    const oreStraordinario = parseFloat(dayEntry.overtime) || 0;
    const oreTotali = oreStandard + oreStraordinario;

    return {
      found: true,
      isSpecialDay: false,
      oreStandard: oreStandard,
      oreStraordinario: oreStraordinario,
      oreTotali: oreTotali,
      note: dayEntry.notes || ''
    };

  } catch (error) {
    console.error(`❌ Errore getWorkHoursForDate:`, error);
    return {
      found: false,
      reason: `Errore: ${error.message}`,
      isSpecialDay: false,
      oreStandard: 0,
      oreStraordinario: 0,
      oreTotali: 0
    };
  }
};

/**
 * 🔍 HELPER: Controlla discrepanze ore
 */
const checkHoursDiscrepancy = async (rapportino, userId) => {
  try {
    // 🔧 FIX: Usa formatDateToYYYYMMDD per conversione corretta
    const dataRapportino = formatDateToYYYYMMDD(rapportino.data);
    
    const workHoursData = await getWorkHoursForDate(userId, dataRapportino);

    if (workHoursData.isSpecialDay) {
      return { 
        hasDiscrepancy: false, 
        workHoursData,
        reason: 'Giorno speciale' 
      };
    }

    const persona = rapportino.personalePresente?.find(p => 
      (p.personaId || p.userId) === userId
    );

    if (!persona) {
      return { 
        hasDiscrepancy: false, 
        workHoursData: null,
        reason: 'Persona non trovata' 
      };
    }

    const oreRapportino = (parseFloat(persona.oreLavorate) || 0);
    const oreWorkHours = workHoursData.oreTotali;

    const hasDiscrepancy = Math.abs(oreRapportino - oreWorkHours) > 0.01;

    return { 
      hasDiscrepancy, 
      workHoursData,
      oreRapportino,
      oreWorkHours,
      reason: hasDiscrepancy ? `Diff ${Math.abs(oreRapportino - oreWorkHours).toFixed(2)}h` : 'Allineate'
    };

  } catch (error) {
    console.error('❌ Errore checkHoursDiscrepancy:', error);
    return { 
      hasDiscrepancy: false, 
      workHoursData: null,
      reason: `Errore: ${error.message}` 
    };
  }
};

/**
 * 🎯 FUNZIONE PRINCIPALE
 */
export const syncAllRapportiniCosts = async (options = {}) => {
  const {
    dryRun = false,
    onlyZeroCost = true,
    syncHours = false,
    startDate = null,
    endDate = null,
    onProgress = null,
    batchSize = 50
  } = options;

  console.log('🚀 [v2.4 FINAL] Sincronizzazione rapportini...');
  console.log(`   DryRun: ${dryRun}, SyncHours: ${syncHours}`);

  const stats = {
    totaleRapportini: 0,
    rapportiniDaAggiornare: 0,
    rapportiniAggiornati: 0,
    personaleAggiornato: 0,
    costiAggiornati: 0,
    oreAggiornate: 0,
    oreAzzerateCorrettamente: 0,
    discrepanzeOre: 0,
    errori: 0,
    dettagli: []
  };

  try {
    // 1️⃣ Carica utenti
    const users = await getAllUsers();
    const usersMap = new Map(users.map(u => [u.id, u]));
    console.log(`✅ Caricati ${users.length} utenti`);

    // 2️⃣ Carica rapportini
    const rapportiniRef = collection(db, 'rapportini');
    let queryConstraints = [where('stato', '==', 'Attivo')];
    
    if (startDate) {
      const startDateObj = startDate instanceof Date ? startDate : new Date(startDate);
      queryConstraints.push(where('data', '>=', startDateObj));
    }
    
    if (endDate) {
      const endDateObj = endDate instanceof Date ? endDate : new Date(endDate);
      endDateObj.setDate(endDateObj.getDate() + 1);
      queryConstraints.push(where('data', '<', endDateObj));
    }
    
    const q = query(rapportiniRef, ...queryConstraints);
    const snapshot = await getDocs(q);
    
    stats.totaleRapportini = snapshot.size;
    console.log(`✅ Trovati ${stats.totaleRapportini} rapportini`);

    // 3️⃣ Analizza rapportini
    const rapportiniDaAggiornare = [];
    
    for (const docSnap of snapshot.docs) {
      const rapportino = docSnap.data();
      const rapportinoId = docSnap.id;
      const personalePresente = rapportino.personalePresente || [];
      
      let shouldUpdate = false;

      if (syncHours) {
        for (const persona of personalePresente) {
          const userId = persona.personaId || persona.userId;
          if (!userId) continue;

          const { hasDiscrepancy } = await checkHoursDiscrepancy(
            { ...rapportino, personalePresente }, 
            userId
          );

          if (hasDiscrepancy) {
            stats.discrepanzeOre++;
            shouldUpdate = true;
            break;
          }
        }
      } else {
        const hasZeroCost = personalePresente.some(p => {
          const costoOrario = parseFloat(p.costoOrario || 0);
          const totale = parseFloat(p.totale || 0);
          const oreLavorate = (parseFloat(p.oreLavorate) || 0);
          return oreLavorate > 0 && (costoOrario === 0 || totale === 0);
        });

        shouldUpdate = !onlyZeroCost || hasZeroCost;
      }

      if (shouldUpdate) {
        rapportiniDaAggiornare.push({
          id: rapportinoId,
          data: rapportino.data,
          cantiereId: rapportino.cantiereId,
          personalePresente: personalePresente
        });
      }
    }

    stats.rapportiniDaAggiornare = rapportiniDaAggiornare.length;
    console.log(`📊 Rapportini da aggiornare: ${stats.rapportiniDaAggiornare}`);

    if (rapportiniDaAggiornare.length === 0) {
      console.log('✅ Nessun aggiornamento necessario');
      return stats;
    }

    // 4️⃣ Aggiorna rapportini
    if (!dryRun) {
      console.log('🔄 Inizio aggiornamento...');
      
      for (let i = 0; i < rapportiniDaAggiornare.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchRapportini = rapportiniDaAggiornare.slice(i, i + batchSize);
        
        for (const rapportino of batchRapportini) {
          try {
            const updatedPersonale = [];
            let rapportinoModificato = false;
            
            for (const persona of rapportino.personalePresente) {
              const user = usersMap.get(persona.personaId || persona.userId);
              
              if (!user) {
                updatedPersonale.push(persona);
                continue;
              }

              const costoOrario = parseFloat(user.costoOrario || 0);
              let oreLavorate = (parseFloat(persona.oreLavorate) || 0);
              let oreStandard = parseFloat(persona.oreStandard || persona.oreOrdinarie || 0);
              let oreStraordinario = parseFloat(persona.oreStraordinario || persona.oreStraordinarie || 0);
              
              const oldOre = oreLavorate;
              const oldTotal = parseFloat(persona.totale || 0);
              
              let oreAggiornate = false;
              
              if (syncHours) {
                // 🔧 FIX: Usa formatDateToYYYYMMDD
                const dataRapportino = formatDateToYYYYMMDD(rapportino.data);
                
                const workHoursData = await getWorkHoursForDate(
                  persona.personaId || persona.userId, 
                  dataRapportino
                );

                if (workHoursData && !workHoursData.isSpecialDay) {
                  const oreWorkHours = workHoursData.oreTotali;
                  
                  if (Math.abs(oreLavorate - oreWorkHours) > 0.01) {
                    console.log(`   🔄 ${persona.nome}: ${oreLavorate}h → ${oreWorkHours}h`);
                    
                    oreStandard = workHoursData.oreStandard;
                    oreStraordinario = workHoursData.oreStraordinario;
                    oreLavorate = oreWorkHours;
                    oreAggiornate = true;
                    stats.oreAggiornate++;
                    rapportinoModificato = true;

                    if (oreWorkHours === 0 && oldOre > 0) {
                      stats.oreAzzerateCorrettamente++;
                    }
                  }
                }
              }

              const newTotal = oreLavorate * costoOrario;
              
              if (Math.abs(oldTotal - newTotal) > 0.01 || oreAggiornate) {
                rapportinoModificato = true;
                if (Math.abs(oldTotal - newTotal) > 0.01) stats.costiAggiornati++;
                stats.personaleAggiornato++;
                
                if (stats.dettagli.length < 20) {
                  stats.dettagli.push({
                    persona: `${persona.nome} ${persona.cognome}`,
                    vecchioOre: oldOre,
                    nuovoOre: oreLavorate,
                    vecchioCosto: oldTotal,
                    nuovoCosto: newTotal,
                    ore: oreLavorate,
                    costoOrario: costoOrario,
                    oreAggiornate: oreAggiornate,
                    azzerateCorrettamente: (oreAggiornate && oreLavorate === 0 && oldOre > 0)
                  });
                }
              }

              updatedPersonale.push({
                ...persona,
                costoOrario: costoOrario,
                oreLavorate: oreLavorate,
                oreStandard: oreStandard,
                oreStraordinario: oreStraordinario,
                totale: newTotal,
                syncedAt: new Date().toISOString()
              });
            }

            if (rapportinoModificato) {
              const rapportinoRef = doc(db, 'rapportini', rapportino.id);
              batch.update(rapportinoRef, {
                personalePresente: updatedPersonale,
                lastCostSync: new Date(),
                lastHoursSync: syncHours ? new Date() : null,
                syncedBy: 'syncAllRapportiniCosts_v2.4_TimestampFix'
              });

              stats.rapportiniAggiornati++;
            }

          } catch (error) {
            console.error(`❌ Errore rapportino ${rapportino.id}:`, error);
            stats.errori++;
          }
        }

        try {
          await batch.commit();
          console.log(`✅ Batch ${Math.floor(i / batchSize) + 1} completato`);
          
          if (onProgress) {
            onProgress({
              processati: Math.min(i + batchSize, rapportiniDaAggiornare.length),
              totali: rapportiniDaAggiornare.length,
              percentuale: Math.round((Math.min(i + batchSize, rapportiniDaAggiornare.length) / rapportiniDaAggiornare.length) * 100)
            });
          }
        } catch (error) {
          console.error(`❌ Errore commit:`, error);
          stats.errori++;
        }
      }
    } else {
      // DRY RUN
      console.log('🔍 DRY RUN - Primi 5:');
      
      for (const rapportino of rapportiniDaAggiornare.slice(0, 5)) {
        console.log(`\n📄 Rapportino ${rapportino.id}:`);
        
        for (const persona of rapportino.personalePresente) {
          const user = usersMap.get(persona.personaId || persona.userId);
          if (!user) continue;

          const costoOrario = parseFloat(user.costoOrario || 0);
          let oreLavorate = (parseFloat(persona.oreLavorate) || 0);
          
          if (syncHours) {
            const dataRapportino = formatDateToYYYYMMDD(rapportino.data);
            const workHoursData = await getWorkHoursForDate(
              persona.personaId || persona.userId, 
              dataRapportino
            );

            if (workHoursData && !workHoursData.isSpecialDay) {
              oreLavorate = workHoursData.oreTotali;
            }
          }

          const totale = oreLavorate * costoOrario;
          console.log(`   💰 ${persona.nome}: ${oreLavorate}h × €${costoOrario}/h = €${totale.toFixed(2)}`);
        }
      }
    }

    // 5️⃣ Riepilogo
    console.log('\n' + '='.repeat(50));
    console.log('📊 RIEPILOGO');
    console.log('='.repeat(50));
    console.log(`Totale: ${stats.totaleRapportini}`);
    console.log(`Da aggiornare: ${stats.rapportiniDaAggiornare}`);
    if (!dryRun) {
      console.log(`Aggiornati: ${stats.rapportiniAggiornati}`);
      console.log(`Personale: ${stats.personaleAggiornato}`);
      console.log(`Costi: ${stats.costiAggiornati}`);
      if (syncHours) {
        console.log(`Ore aggiornate: ${stats.oreAggiornate}`);
        console.log(`Ore azzerate: ${stats.oreAzzerateCorrettamente}`);
        console.log(`Discrepanze: ${stats.discrepanzeOre}`);
      }
      console.log(`Errori: ${stats.errori}`);
    }
    console.log('='.repeat(50));

    return stats;

  } catch (error) {
    console.error('❌ Errore sincronizzazione:', error);
    throw error;
  }
};

/**
 * 🎯 HELPER: Sincronizza singolo rapportino
 */
export const syncSingleRapportinoCosts = async (rapportinoId, syncHours = false) => {
  try {
    const users = await getAllUsers();
    const usersMap = new Map(users.map(u => [u.id, u]));

    const rapportinoRef = doc(db, 'rapportini', rapportinoId);
    const rapportinoSnap = await getDoc(rapportinoRef);

    if (!rapportinoSnap.exists()) {
      throw new Error(`Rapportino ${rapportinoId} non trovato`);
    }

    const rapportino = rapportinoSnap.data();
    const personalePresente = rapportino.personalePresente || [];

    const updatedPersonale = [];
    for (const persona of personalePresente) {
      const user = usersMap.get(persona.personaId || persona.userId);
      
      if (!user) {
        updatedPersonale.push(persona);
        continue;
      }

      const costoOrario = parseFloat(user.costoOrario || 0);
      let oreLavorate = (parseFloat(persona.oreLavorate) || 0);
      let oreStandard = parseFloat(persona.oreStandard || persona.oreOrdinarie || 0);
      let oreStraordinario = parseFloat(persona.oreStraordinario || persona.oreStraordinarie || 0);

      if (syncHours) {
        const dataRapportino = formatDateToYYYYMMDD(rapportino.data);
        const workHoursData = await getWorkHoursForDate(
          persona.personaId || persona.userId, 
          dataRapportino
        );

        if (workHoursData && !workHoursData.isSpecialDay) {
          oreStandard = workHoursData.oreStandard;
          oreStraordinario = workHoursData.oreStraordinario;
          oreLavorate = workHoursData.oreTotali;
        }
      }

      const totale = oreLavorate * costoOrario;

      updatedPersonale.push({
        ...persona,
        costoOrario: costoOrario,
        oreLavorate: oreLavorate,
        oreStandard: oreStandard,
        oreStraordinario: oreStraordinario,
        totale: totale,
        syncedAt: new Date().toISOString()
      });
    }

    await updateDoc(rapportinoRef, {
      personalePresente: updatedPersonale,
      lastCostSync: new Date(),
      lastHoursSync: syncHours ? new Date() : null,
      syncedBy: 'syncAllRapportiniCosts_v2.4_TimestampFix'
    });

    console.log(`✅ Rapportino ${rapportinoId} sincronizzato`);
    return true;

  } catch (error) {
    console.error(`❌ Errore rapportino ${rapportinoId}:`, error);
    throw error;
  }
};

export default syncAllRapportiniCosts;